import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, ClipboardList, ShieldCheck, HardHat, Camera, FileSpreadsheet,
  Bell, Languages, Scale, Users, Building2, BadgeCheck, X,
} from "lucide-react";
import Seo, { breadcrumbLd, faqLd, SITE_URL } from "@/components/Seo";
import { MarketingNav, MarketingFooter, MarketingCTA } from "@/components/MarketingChrome";

const CHAIN = [
  {
    icon: ClipboardList, tag: "Intake",
    title: "The request becomes one coded work order",
    body: "A resident's text, a board decision, a recurring visit or an awarded project, all of it lands on the same spine, with a budget code attached. Nothing gets promised to a vendor that your books can't see.",
  },
  {
    icon: ShieldCheck, tag: "Vendor",
    title: "It only goes to a company that's actually current",
    body: "Certificates of insurance, W-9 and the signed agreement live on the vendor record with expiry dates. A company can come out for a site visit with nothing on file, that keeps the funnel alive, but live work won't dispatch to an uninsured crew.",
  },
  {
    icon: HardHat, tag: "Dispatch",
    title: "The crew on that property gets a text",
    body: "Not the office, then a phone tree, then a callback. The assigned crew member, in English or Spanish, with the address and the scope. They can reply from the truck, or call the community line and talk to the concierge.",
  },
  {
    icon: Camera, tag: "Proof",
    title: "A photo closes the resident's ticket",
    body: "The crew sends a picture when it's done. That photo attaches to the work order and closes the resident's request automatically, so the person who reported it hears back without a manager typing anything.",
  },
  {
    icon: FileSpreadsheet, tag: "Money",
    title: "The invoice arrives read, checked and coded",
    body: "Invoices are read on arrival and checked against the work order, scope, amount, proof. A manager approves, the board signs anything above the community's threshold, and it exports to your accounting system already coded. Redline never moves the money.",
  },
];

const GATES = [
  {
    icon: ShieldCheck, title: "Insurance gates the work, not an inbox",
    body: "Most systems email you a reminder that a certificate expired. This one stops the job. A lapsed COI pauses live work until it's replaced, and the chase starts before the expiry date, not after.",
  },
  {
    icon: Scale, title: "Kickoff won't start without a budget line",
    body: "When a project is awarded, the kickoff refuses to proceed until an accounting code is chosen. That's the difference between a spend the board can explain and one that shows up as a surprise.",
  },
  {
    icon: BadgeCheck, title: "The board signs above your threshold",
    body: "Set the dollar figure per community. Under it, a manager approves and it moves. Over it, the invoice waits for a board signature, recorded, timestamped, and impossible to skip quietly.",
  },
];

const AUDIENCES = [
  {
    icon: Users, who: "The manager",
    body: "One board for every open job across your communities: who's assigned, who hasn't accepted, what's waiting on paperwork, what's waiting on you. The chasing you do by phone today happens on a schedule instead.",
  },
  {
    icon: HardHat, who: "The crew",
    body: "A text with the address and the scope. Reply \"done\" with a photo, or call and speak to it, in English or Spanish. No app, no portal, no login, no training day.",
  },
  {
    icon: Building2, who: "The vendor's office",
    body: "A link. They fill in their trades, coverage area, insurance and W-9 at their own pace, e-sign the agreement, and can bid on posted projects from a shared link without creating an account first.",
  },
  {
    icon: Scale, who: "The board",
    body: "What was spent, what needed their signature, and photo proof it happened, without anyone chasing a manager for a status update before the meeting.",
  },
];

const NOT = [
  "A vendor marketplace. We don't sell you leads or rank companies you've never met, it runs on your vendor list, plus anyone you invite.",
  "A payment processor. Redline never touches your money. Approved invoices export to your accounting system; you pay vendors the way you always have.",
  "A replacement for your accounting software. It's the layer that makes sure what reaches your accountant is already coded and already approved.",
];

const FAQS = [
  {
    q: "What is HOA field services software?",
    a: "It's the layer between a resident's request and the vendor who fixes it. A request becomes a work order, the work order goes to a vendor whose insurance is current, the crew gets dispatched and sends proof, and the invoice comes back checked and coded. In Ask Redline it sits in the same platform that answers residents by text and phone, so the answer and the fix are one thread instead of two systems.",
  },
  {
    q: "How does certificate of insurance (COI) tracking work?",
    a: "Each vendor record holds their certificate, its expiry date, general liability and workers' comp status, and whether the association is named as additional insured. The chase starts before the expiry date. If a certificate lapses, live work stops dispatching to that company until a current one is on file, a site visit is still allowed, because looking at a job isn't doing it.",
  },
  {
    q: "Do our vendors have to create an account or download anything?",
    a: "No. A vendor gets a link and fills in their details at their own pace, trades, coverage area, insurance, W-9, and e-signs the agreement in the browser. They can even submit a bid on a posted project from a shared link before finishing any paperwork, because a paperwork wall at the bidding stage just means fewer bids. The paperwork is required before work is awarded and started.",
  },
  {
    q: "How do crews get their jobs?",
    a: "By text, to the crew member assigned to that property, in English or Spanish. They can reply with a status and a photo, or call the community's number and speak to the concierge. There's no app to install, which matters when the person doing the work is standing on a roof.",
  },
  {
    q: "Can we put a project out for bids?",
    a: "Yes. Post the scope, the AI will draft one from the request history if you'd like, and target it by trade, coverage area, insurance status or past rating. It also appears on a public work board that needs no login. When you award it, the kickoff won't start until a budget code is chosen, and the contract goes out for e-signature.",
  },
  {
    q: "Does Ask Redline pay our vendors?",
    a: "No, and it never will. Invoices are read, checked against the work order, approved by a manager, signed by the board above your threshold, and exported to your accounting system already coded. The money movement stays with you and your bank.",
  },
  {
    q: "Will it work with our existing accounting system?",
    a: "Yes. Accounting codes come from a company-wide template that each community can override, and approved invoices export in a batch your accountant can import. Field Services is the approval and proof layer, not a general ledger.",
  },
  {
    q: "Is Field Services available for a single community, or only portfolios?",
    a: "Either. It's an add-on module enabled per community, so one self-managed association can run it, and a management company can turn it on for the communities that need it. Book a demo and we'll walk it through on your own vendor list.",
  },
];

const RELATED = [
  ["/solutions/hoa-management-companies", "For management companies"],
  ["/solutions/hoa-communication-software", "HOA communication software"],
  ["/solutions/self-managed-hoa-software", "For self-managed HOAs"],
];

const TITLE = "HOA Vendor Management & Work Order Software, Ask Redline Field Services";
const DESC = "Vendor management, work orders and invoice approval for HOAs and community associations. Insurance gates live work, crews get texted in English or Spanish, photo proof closes the resident's ticket, and invoices export already coded.";

export default function FieldServicesPage() {
  const jsonLd = [
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Field services", path: "/field-services" },
    ]),
    faqLd(FAQS),
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Ask Redline Field Services",
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Vendor & work order management for community associations",
      operatingSystem: "Web, SMS and voice, no app required",
      description: DESC,
      publisher: { "@type": "Organization", name: "Ask Redline", url: SITE_URL },
    },
  ];

  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen">
      <Seo title={TITLE} description={DESC} canonical="/field-services" jsonLd={jsonLd} scrollTop />
      <MarketingNav />

      {/* HERO */}
      <section className="border-b border-sand-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-14 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
              <HardHat className="h-3.5 w-3.5" strokeWidth={2} /> Field services · vendors &amp; work orders
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]" data-testid="fs-page-title">
              Vendor management that ends at a coded invoice,<br className="hidden sm:block" />{" "}
              <span className="text-redline-500">not another round of phone tag.</span>
            </h1>
            <p className="mt-5 text-lg text-ink-700 leading-relaxed">
              Answering the resident is half the job. Somebody still has to find a vendor who's insured, get the
              right crew to the right address, prove it happened, and turn the invoice into something your
              accountant can post. Field Services runs that whole chain in the same platform that answers your
              residents by text and phone.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="/#contact" data-testid="fs-page-demo"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-redline-500 text-white font-semibold px-6 py-3 hover:bg-redline-600 transition-colors">
                See it on your vendors <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </a>
              <Link to="/vendor-network" data-testid="fs-page-vendor-cta"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-sand-200 bg-white font-semibold px-6 py-3 hover:border-ink transition-colors">
                Are you a vendor? Apply to join
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="max-w-3xl">
          <p className="text-lg text-ink-700 leading-relaxed mb-5">
            Ask any community manager where the day goes and it isn't answering questions, it's chasing. Which
            vendor covers this trade. Whether their certificate is still current. Who's actually going out on
            Tuesday. Whether it got done. Where the invoice went, and which budget line it belongs to.
          </p>
          <p className="text-lg text-ink-700 leading-relaxed mb-5">
            Every one of those is a handoff, and every handoff is a place where a promise to a resident quietly
            dies. Field Services removes the handoffs: one work order carries the job from the moment it's raised
            to the moment it's paid, and each gate along the way either holds or tells someone.
          </p>
          <p className="text-lg text-ink-700 leading-relaxed">
            It's an add-on module, enabled per community, for HOAs, condo associations and the management
            companies that run them.
          </p>
        </div>
      </section>

      {/* THE CHAIN */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">One thread, from the text to the invoice</h2>
        <p className="text-ink-600 mb-8 max-w-2xl">Five stages, one record. Nothing is retyped into a second system.</p>
        <ol className="relative border-l border-sand-300 ml-5 space-y-8">
          {CHAIN.map((s, i) => (
            <motion.li key={s.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
              data-testid={`fs-page-chain-${i}`} className="relative pl-8 sm:pl-10">
              <span className="absolute -left-[17px] top-0 h-[34px] w-[34px] rounded-xl bg-white border border-sand-300 flex items-center justify-center shadow-sm">
                <s.icon className="h-4 w-4 text-redline-600" strokeWidth={1.75} />
              </span>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-ink-500">{s.tag}</p>
              <h3 className="font-heading text-lg md:text-lg font-extrabold tracking-tight mt-1.5">{s.title}</h3>
              <p className="text-ink-700 text-sm leading-relaxed mt-2 max-w-2xl">{s.body}</p>
            </motion.li>
          ))}
        </ol>
      </section>

      {/* THE GATES */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Three gates that actually hold</h2>
        <p className="text-ink-600 mb-8 max-w-2xl">A rule that only sends a reminder isn't a rule. These stop the job.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {GATES.map((g, i) => (
            <motion.div key={g.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: i * 0.06 }}
              data-testid={`fs-page-gate-${i}`}
              className="rounded-3xl border border-sand-200 bg-white p-7 hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(15,15,20,0.25)] transition-all">
              <div className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center mb-4">
                <g.icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-lg font-extrabold tracking-tight mb-2">{g.title}</h3>
              <p className="text-ink-700 text-sm leading-relaxed">{g.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHO SEES WHAT */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="relative overflow-hidden rounded-3xl bg-ink text-white p-8 sm:p-12">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-redline-500/20 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Four people, four views, one job</h2>
            <p className="text-sand-300 mb-8 max-w-2xl">Everybody sees exactly their part of it, and nothing they shouldn't.</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {AUDIENCES.map((a) => (
                <div key={a.who} data-testid={`fs-page-audience-${a.who.split(" ").pop().toLowerCase()}`}
                  className="rounded-2xl bg-white/[0.06] border border-white/10 p-6 backdrop-blur">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-xl bg-redline-500/20 flex items-center justify-center">
                      <a.icon className="h-4 w-4 text-redline-400" strokeWidth={1.75} />
                    </div>
                    <div className="font-heading font-bold text-white">{a.who}</div>
                  </div>
                  <p className="text-sand-300 text-sm leading-relaxed">{a.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NOTHING ROTS */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="rounded-3xl border border-sand-200 bg-white p-8 sm:p-10" data-testid="fs-page-nothing-rots">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center shrink-0">
              <Bell className="h-5 w-5 text-redline-600" strokeWidth={1.75} />
            </div>
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight">Nothing is allowed to rot</h2>
              <p className="text-ink-700 leading-relaxed mt-3 max-w-3xl">
                A project that goes quiet with no bids. A company half-way through setup. A certificate about to
                lapse. A job nobody accepted. Each one gets chased on a schedule, and if it's still sitting there,
                it gets flagged for a person by name. Silence is never mistaken for progress, which is the
                failure mode of every spreadsheet this replaces.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-600">
                {["Bid deadlines", "Stale projects", "Unfinished vendor setup", "Expiring insurance", "Unaccepted dispatches"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-redline-500" strokeWidth={2.5} /> {t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HONEST SCOPE */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">What it isn't</h2>
        <p className="text-ink-600 mb-6">Worth being straight about, so a demo doesn't surprise you.</p>
        <ul className="space-y-3 max-w-3xl">
          {NOT.map((n) => (
            <li key={n} className="flex items-start gap-3 rounded-2xl border border-sand-200 bg-white p-5">
              <X className="h-5 w-5 text-ink-400 shrink-0 mt-0.5" strokeWidth={2.5} />
              <span className="text-ink-700 text-sm leading-relaxed">{n}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* BILINGUAL NOTE */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="rounded-3xl bg-sand-100 border border-sand-200 p-7 flex items-start gap-4 max-w-4xl">
          <Languages className="h-6 w-6 text-redline-600 shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="text-ink-700 leading-relaxed">
            <span className="font-semibold text-ink">A note on language.</span> The crews doing the work often
            aren't working in English, and asking them to is how details get lost. Dispatch texts, inbound
            replies and phone calls all work in English and Spanish, on the same job record.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">Frequently asked</h2>
        <div className="space-y-3 max-w-3xl">
          {FAQS.map((f, i) => (
            <div key={i} className="rounded-2xl border border-sand-200 bg-white p-5" data-testid={`fs-page-faq-${i}`}>
              <div className="font-heading font-bold text-ink">{f.q}</div>
              <p className="text-ink-700 text-sm mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RELATED */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink-400 mb-4">Keep reading</div>
        <div className="grid sm:grid-cols-3 gap-4">
          {RELATED.map(([to, label]) => (
            <Link key={to} to={to} data-testid={`fs-page-related-${to.split("/").pop()}`}
              className="group rounded-2xl border border-sand-200 bg-white p-5 hover:border-redline-300 transition-colors">
              <div className="font-heading font-bold text-ink group-hover:text-redline-600 transition-colors">{label}</div>
              <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-redline-600">Learn more <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      <MarketingCTA
        title="Bring your worst vendor story to the demo."
        sub="We'll run it through the chain live, the certificate that lapsed, the job nobody accepted, the invoice with no budget code, and you can see exactly where it would have stopped."
      />
      <MarketingFooter />
    </div>
  );
}
